import {Component, ContentChildren, ElementRef, EventEmitter, Input, Output, QueryList} from '@angular/core';
import {ApiService, FieldDefineGroup, LanguagesService, MessageService} from '../../../core';
import {FieldType, FormDefine, FormServerData, FormStatus, serialParamName, ServiceCode} from '../../../core';
import {Util} from '../../../core';
import {UserFieldDirective} from '../../directive';

@Component({
  template: ''
})
export abstract class BaseFormComponent {

  protected constructor(protected api: ApiService, protected message: MessageService,
                        public lang: LanguagesService, protected el: ElementRef) {

  }

  @Input()
  changeToViewAfterUpdate = true;

  @Input()
  priorInlineUpdate = true;

  @Input()
  get apiUrl(): string {
    return this.myApiUrl;
  }

  set apiUrl(v) {
    this.myApiUrl = v;
    this.init();
  }


  loading = true;

  status: FormStatus = FormStatus.View;

  // Editing info
  editCache: { [key: number]: { delete: boolean, edit: boolean; data: { [key: string]: any } } } = {};

  // Form define
  define: FormDefine;

  // Form data
  data: Array<any>;

  // Extra data
  extra: any = null;

  formHeight = null;

  // list: {[key: string]: Array<any>};
  @Input() links: { [field: string]: (record: any) => any };
  @Input() validators: { [field: string]: ((record: any) => string)[] };

  protected myApiUrl: string;

  @Input() auto = true;
  @Input() showButtons = true;
  @Input() showBackButton = false;
  @Input() params: {[key: string]: any} = {};
  @Input() height: number = null;
  @Input() bottomDistance = 52;

  @ContentChildren(UserFieldDirective) userFieldDirectives: QueryList<UserFieldDirective>;
  userFields: { [key: string]: UserFieldDirective } = {};

  @Output()
  loaded = new EventEmitter();

  @Output()
  defined = new EventEmitter();

  @Output()
  updated = new EventEmitter<{[key: string]: any}[]>();

  @Output()
  inserted = new EventEmitter<{[key: string]: any}[]>();

  @Output()
  deleted = new EventEmitter<number>();

  @Input()
  defineLoaded: () => void = () => {}

  @Input()
  dataLoaded: () => void = () => {}

  @Input()
  dataDeleted: (data: number) => void = (_) => {}

  @Input()
  dataUpdated: (data: any[]) => void = (_) => {}

  @Input()
  dataInserted: (data: any[]) => void = (_) => {}

  protected init(): void {

  }

  public load(OnLoaded: () => void = null, params: {[key: string]: any} = null ): void {
    this.loading = true;
    // Have nothing to do
    if (!this.define) {
      return;
    }
    if (!params) {
      params = this.params;
    }
    this.api.data(this.apiUrl, this.define, params,
      ((code, message, data: FormServerData) => {
        // tslint:disable-next-line:triple-equals
        if (code !== 200) {
          this.data = [];
          this.loading = false;
          if (OnLoaded) {
            OnLoaded();
          }
          this.error(message);
          return;
        }
        this.data = data.data;
        this.define.total = data.total;
        this.define.summaries = data.summary;
        this.extra = data.extra;
        if (this.extra && this.extra.debug) {
          if (this.extra.debug.time) {
            console.log(this.extra.debug.time);
          }
          if (this.extra.debug.query) {
            console.log(this.extra.debug.query);
          }
        }
        if (data.list) {
          if (this.define.optionsMap && Object.keys(this.define.optionsMap).length > 0){
            Object.keys(data.list).forEach(key => {
              this.define.optionsMap[key] = data.list[key].slice();
            });
          }else{
            this.define.optionsMap = data.list;
          }
          // this.define.optionsMap = {};
          // Object.keys(data.list).forEach(key => {
          //   this.define.optionsMap[key] = Util.toKendoListValues(data.list[key], 'base-form');
          // });
        }else{
          this.define.optionsMap = {};
        }
        let i = 1;
        this.data.forEach(item => {
          item.serial = i;
          i++;

          // Format date
          this.define.fields.forEach(
            (field) => {
              if (item[field.name]) {
                if ((field.type === FieldType.Date || field.type === FieldType.DateTime)) {
                  item[field.name] = Util.toDatetime(item[field.name]);
                } else if (field.type === FieldType.Int || field.type === FieldType.Float) {
                  if (!field.autocomplete && !field.choices) {
                    item[field.name] = +item[field.name];
                  }
                }
              }
            }
          );
        });
        this.loading = false;
        if (OnLoaded) {
          OnLoaded();
        }
      }));
  }

  protected loadFieldGroups(): FieldDefineGroup [] {
    const ret: FieldDefineGroup[] = [];
    if (!this.define && !this.define.fields) {
      return ret;
    }

    let fieldGroup: FieldDefineGroup = {field: null, fields: [], groupName: null};
    let currentGroup = null;
    this.define.fields.forEach(item => {
      item.groupFirstItem = false;
      item.groupFirstEditItem = false;
      if (item.groupName) {
        if (item.editable) {
          if (!currentGroup || currentGroup !== item.groupName){
            item.groupFirstEditItem = true;
            currentGroup = item.groupName;
          }
        }
        if (!fieldGroup.groupName) {
          item.groupFirstItem = true;
          fieldGroup.groupName = item.groupName;
          ret.push(fieldGroup);
        }else if (fieldGroup.groupName !== item.groupName) {
          item.groupFirstItem = true;
          fieldGroup = {field: null, fields: [], groupName: item.groupName};
          ret.push(fieldGroup);
        }
        fieldGroup.fields.push(item);
      }else{
        fieldGroup = {field: null, fields: [], groupName: null};
        ret.push({field: item, fields: [item], groupName: null});
      }
    });

    this.define.fieldGroups = ret;
    return ret;
  }

  updateEditCache(): void {
    this.editCache = {};
    this.data.forEach(item => {
      if (item.serial) {
        this.editCache[item.serial] = {
          edit: false,
          delete: false,
          data: Util.objectAssign({}, item) // {...item}
        };
      }

    });
  }

  countEditCache(deleteMark: boolean = false): number {
    let ret = 0;
    this.data.forEach(item => {
      if (!item.serial) {
        return;
      }
      if ((this.editCache[item.serial].delete && deleteMark) || (this.editCache[item.serial].edit && !deleteMark)) {
        ret = ret + 1;
        return;
      }
    });
    return ret;
  }

  clearEditCache(deleteMark: boolean = false): void {
    if (deleteMark) {
      this.data.forEach(item => {
        if (!item.serial) {
          return;
        }
        if (this.editCache[item.serial].delete) {
          this.editCache[item.serial].delete = false;
        }
      });
    } else {
      this.data.forEach(item => {
        if (!item.serial) {
          return;
        }
        if (this.editCache[item.serial].edit) {
          this.editCache[item.serial].edit = false;
        }
      });
    }
  }

  protected loadUserFields(): void {
    if (!this.userFieldDirectives) {
      return;
    }
    this.userFieldDirectives.forEach(item => {
      this.userFields[item.name] = item;
    });
  }

  public isInsert(data): boolean {
    if (!data || data.length === 0) {
      return false;
    }
    return data[0].new;
  }

  private getSelectedData(serial: number, deleteMark: boolean = false): { [key: string]: any }[] {
    const params: {[key: string]: any }[] = [];
    if ( serial != null ) {
      const index = this.data.findIndex(item => item.serial === serial);
      params.push(this.data[index]);
    } else {

      this.data.forEach(rec => {
        if ((this.editCache[rec.serial].edit && !deleteMark) || (this.editCache[rec.serial].delete && deleteMark)) {
          const index = this.data.findIndex(item => item.serial === rec.serial);
          params.push(this.data[index]);
        }
      });
    }
    return params;
  }

  protected clearValidate(): void {
    this.define.invalidInfo = {};
  }

  protected validate(data: { [key: string]: any }[]): boolean {
    this.clearValidate();
    // Object.keys(this.form.controls).forEach(key => {
    //   this.form.controls[key].markAsDirty();
    //   this.form.controls[key].updateValueAndValidity();
    // });
    let ret = true;
    data.forEach(rec => {
      const invalid = {};
      this.define.fields.forEach(field => {
        if (!field.editable) {
          return;
        }

        // Required
        if (field.required) {
          if (field.type === FieldType.File) {
            const fileFieldName = Util.fileFieldName(field.name);
            const file = rec[fileFieldName];
            if (!file) {
              invalid[field.name] = `${this.lang.define.lib.requiredMessage}`;
            }
          } else {
            if (field.required &&
              (rec[field.name] === '' || rec[field.name] === undefined || rec[field.name] === null)) {
              invalid[field.name] = `${this.lang.define.lib.requiredMessage}`;
            }
          }
        }


        if (this.validators && this.validators[field.name]) {
          // Custom validator
          this.validators[field.name].forEach(func => {
            const message = func(rec);
            if (message != null) {
              invalid[field.name] = message;
            }
          });
        }
      });

      if (Object.keys(invalid).length !== 0) {
        ret = false;
        // Show all record.
        // this.define.fields.forEach(field => {
        //   if (!invalid[field.name]) {
        //     invalid[field.name] = null;
        //   }
        // });
        this.define.invalidInfo[rec.serial] = invalid;
      }

    });

    if (!ret) {
      this.error('Input validate fail. Please check red marked field for detail.');
    }
    return ret;
  }

  public save(serial: number, OnSaved: (code: number, message: string) => void): void {
    const params = this.getSelectedData(serial);
    if (!this.validate(params)) {
      if (OnSaved) {
        OnSaved(ServiceCode.ValidateError, null);
      }
      return;
    }

    const index = this.data.findIndex(item => item.serial === serial);
    this.loading = true;
    this.api.update(this.apiUrl, this.define, this.params, params, ((code, message, data) => {
      const insert = this.isInsert(params);

      if (code === ServiceCode.Success) {
        const keys = [];
        this.define.fields.forEach(field => {
          if (field.key) {
            keys.push(field.name);
          }
        });
        // Update key after insert/copy here
        data.forEach(rec => {
          const updatedIndex = this.data.findIndex(item => item.serial === rec[serialParamName]);
          if (updatedIndex >= 0 ) {
            keys.forEach(key => {
              this.data[updatedIndex][key] = rec[key];
            });
            this.data[updatedIndex].new = false;
          }

          this.define.fields.forEach(field => {
            // Update file
            if (field.type === FieldType.File) {
              this.data[updatedIndex][field.name] = rec[field.name];
            }

            // Update auto complete multi choices
            else if (field.choices && field.autocompleteChoices) {
              this.data[updatedIndex][field.name] = rec[field.name];
              // Force list load from server
              Object.keys(this.define.optionsMap).forEach(key => {
                if (key === field.listName) {
                  this.api.getListValues(key, null, null, null, (result => {
                    this.define.optionsMap[key] = Util.toKendoListValues(result);
                  }));
                }else if (key.startsWith(field.listName)) {
                  this.define.optionsMap[key] = null;
                }
              });
            }
            // Update data
            else {
              this.data[updatedIndex][field.name] = rec[field.name];
            }
          });
        });

        this.info(this.lang.define.lib.updatedMessage);

        // 2021/02/21 Dont change to view after update
        if (this.changeToViewAfterUpdate) {
          if (serial == null ) {
            this.updateEditCache();
            this.status = FormStatus.View;
          } else {
            this.editCache[serial] = {
              edit: false,
              delete: false,
              data: Util.objectAssign({}, this.data[index]) // {...this.data[index]}
            };
            if (this.countEditCache() === 0) {
               this.status = FormStatus.View;
            }
          }
        }
        if (OnSaved) {
          const count = data.length;
          OnSaved(ServiceCode.Success, this.lang.define.lib.updatedMessage + ` (${count})`);
        }

        if (insert) {
          // Update total so no data not show
          if (data.length) {
            this.define.total += data.length;
          }
          this.dataInserted(data);
          this.inserted.emit(data);
        } else {
          this.dataUpdated(data);
          this.updated.emit(data);
        }
      } else {
        this.error(message);
        if (OnSaved) {
          OnSaved(code, message);
        }
      }
      this.loading = false;
    }));
  }

  public action(action: string, inputData: any, OnCompleted: (code: number, message: string, data: any) => void = null): void {
    this.api.action(this.apiUrl, action, this.define, this.params, inputData, ((code, message, data) => {
      if (OnCompleted) {
        OnCompleted(code, message, data);
      }
    }));
  }

  protected initDefaultValue(item: {[key: string]: any}): void {
    this.define.fields.forEach(field => {
      if (field.defaultValue && !item[field.name]) {
        item[field.name] = field.defaultValue;
      }
    });
  }

  public delete(serial: number, OnDeleted: (code: number, message: string) => void): void {
    const params = this.getSelectedData(serial, true);
    this.loading = true;
    this.api.delete(this.apiUrl, this.define, this.params, params, ((code, message, data) => {
      this.loading = false;
      if (code === ServiceCode.Success) {
        // Remove data
        params.forEach(item => {
          this.data = this.data.filter(d => d.serial !== item.serial);
        });
        this.info(this.lang.define.lib.deletedMessage);
        this.clearEditCache();
        if (OnDeleted) {
          OnDeleted(ServiceCode.Success, this.lang.define.lib.deletedMessage + ` (${data})`);
        }

        this.dataDeleted(data);
        this.deleted.emit(data);
      } else {
        this.error(message);
        if (OnDeleted) {
          OnDeleted(code, message);
        }
      }
    }));
  }

  public editable(): boolean {
    return this.status !== FormStatus.View;
  }

  protected serial(): number {
    let ret = 0;
    this.data.forEach(rec => {
      if (ret < rec.serial) {
        ret = rec.serial;
      }
    });
    return ret;
    // if (this.data.length === 0) {
    //   return 0;
    // } else {
    //   return this.data[this.data.length - 1].serial;
    // }
  }

  info(message: string): void {
    this.message.success(message);
  }

  warning(message: string, closeable= false): void {
    this.message.warning(message, 4000, closeable);
  }

  error(message: string, width = 800): void {
    this.message.error(message, width);
  }

  protected clearFileInput(serial: number): void {
    const index = this.data.findIndex(item => item.serial === serial);
    this.define.fields.forEach(field => {
      if (field.type === FieldType.File) {
        delete this.data[index][Util.fileFieldName(field.name)];
      }
    });
  }

  // public updateCreateUpdateDate(data: any[]): void {
  //   data.forEach(rec => {
  //     const index = this.data.findIndex(item => item.serial === rec[serialParamName]);
  //     if (index >= 0) {
  //       this.data[index].date_update = rec.date_update;
  //       this.data[index].date_create = rec.date_create;
  //     }
  //   });
  // }

  // public actionOnEditMode(action: string, validate: boolean = false,
  //                         OnCompleted: (code: number, message: string, data: any) => void = null): void {
  //   const params = this.getSelectedData(null);
  //   if (validate && !this.validate(params)) {
  //     if (OnCompleted) {
  //       OnCompleted(ServiceCode.ValidateError, null, null);
  //     }
  //     return;
  //   }
  //   this.api.action(this.apiUrl, action, this.define, this.params, params, ((code, message, data) => {
  //     if (OnCompleted) {
  //       OnCompleted(code, message, data);
  //     }
  //   }));
  // }

  protected isVisible(): boolean {
    const rect = this.el.nativeElement.getBoundingClientRect();
    return rect.x !== 0 || rect.y !== 0;
  }

  public resize(): void {
    if (!this.define) {
      return;
    }


    if (!this.isVisible()) {
      return;
    }

    if (this.height) {
      this.formHeight = this.height;
      return;
    }

    // Load column size
    if (!this.bottomDistance) {
      this.formHeight = null;
      return;
    }

    const rect = this.el.nativeElement.getBoundingClientRect();

    let y = rect.y;
    if (y < 0) {
      y = 100;
    }

    const height = window.innerHeight - y - this.bottomDistance;
    this.formHeight = height > 200 ? height : 200;

  }
}
